import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Grid,
  TextField,
  Typography,
  FormControl,
  makeStyles,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Select,
} from "@material-ui/core";
import { Country, State, City } from "country-state-city";
import postalCodes from "postal-codes-js";
import { commonInlineStyles, inputFieldStyles } from "../../styles";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";


const useStyles = makeStyles((theme) => ({
  formContainer: { padding: "24px" },
  formTitle: { marginBottom: theme.spacing(4), fontWeight: 500 },
  formField: {
    width: "100%",
    "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
  },
  button: { minWidth: 180, fontWeight: 600, fontSize: 16 },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "14px",
    marginTop: theme.spacing(0.5),
  },
  label: { marginBottom: theme.spacing(1), fontWeight: 500 },
  readOnlyValue: {
    padding: theme.spacing(1),
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
    minHeight: 40,
    display: "flex",
    alignItems: "center",
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(4),
    textAlign: 'center',
  },
  titleText: {
    fontSize: 20,
  },
  noButton: {
    borderRadius: '2rem',
    padding: '3px 20px',
    backgroundColor: '#fff',
    color: '#000',
    textTransform: 'none',
    border: '1px solid #000',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  yesButton: {
    borderRadius: '2rem',
    padding: '3px 20px',
    backgroundColor: '#ec6535',
    color: '#fff',
    textTransform: 'none',
    marginRight: theme.spacing(2),
    '&:hover': {
      backgroundColor: '#d85a2f',
    },
  },
}));

// Validation functions matching the stepper steps
const validateCmoFullName = (value) => /^[a-zA-ZÀ-ÿ0-9\s&\-,./'()]*$/.test(value);
const validateCmoShortName = (value) => /^[a-zA-Z0-9\-_]*$/.test(value);
const validateStreetAddress = (value) => /^[a-zA-ZÀ-ÿ0-9\s&.,\-/]*$/.test(value);

const statusOptions = ["Active", "Inactive"];

const CMODetailsSection = forwardRef(
  (
    {
      initialData = {},
      onSave,
      onChange,
      vendorMosymphonyID,
      brandOwnerMosymphonyID,
      saasUserId,
      editMode: initialEditMode = false,
      onEditModeChange,
      disableEdit = false,
      onDiscard,
    },
    ref
  ) => {
    const classes = useStyles();
    // Initialize state from initialData or from the ref if we're already in edit mode
    const [form, setForm] = useState({ ...initialData });
    const [errors, setErrors] = useState({});
    const [editMode, setEditMode] = useState(initialEditMode);
    // Use ref to preserve editMode state across renders
    const editModeRef = useRef(false);

    // Only sync with external editMode prop (no callbacks to parent)
    useEffect(() => {
      // Only update internal state if it differs from the prop
      if (editMode !== initialEditMode) {
        setEditMode(initialEditMode);
        editModeRef.current = initialEditMode;
      }
    }, [initialEditMode]);

    // Only notify parent when internal state changes (not from prop sync)
    useEffect(() => {
      // Skip initial render
      if (!isMounted.current) return;

      // Skip if change came from prop
      if (editMode === initialEditMode) return;

      if (onEditModeChange) {
        onEditModeChange(editMode);
      }
    }, [editMode, onEditModeChange, initialEditMode]);

    // Expose the form data and validation functions to the parent component
    useImperativeHandle(ref, () => ({
      getFormData: () => form,
      getPayload: () => createPayload(),
      validateForm: () => validate(),
      resetForm: () => {
        setForm({ ...initialData });
        setErrors({});
      },
    }));

    // Find initial country code
    const initialCountryCode = (() => {
      if (initialData.country) {
        const countries = Country.getAllCountries();
        const matchedCountry = countries.find(
          (c) =>
            c.name === initialData.country || c.isoCode === initialData.country
        );
        return matchedCountry?.isoCode || "";
      }
      return "";
    })();

    // Track country and state codes
    const [countryCode, setCountryCode] = useState(initialCountryCode);

    // Find initial states based on country
    const initialStates = initialCountryCode
      ? State.getStatesOfCountry(initialCountryCode)
      : [];

    // Find initial state code
    const initialStateCode = (() => {
      if (initialData.state && initialCountryCode) {
        const matchedState = initialStates.find(
          (s) => s.name === initialData.state || s.isoCode === initialData.state
        );
        return matchedState?.isoCode || "";
      }
      return "";
    })();

    const [stateCode, setStateCode] = useState(initialStateCode);
    const [states, setStates] = useState(initialStates);
    const [cities, setCities] = useState([]);
    const [postalCodeError, setPostalCodeError] = useState("");
    // Dialog state
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingStatus, setPendingStatus] = useState(null);
    // Track if form has been modified
    const [isModified, setIsModified] = useState(false);
    // Store original data for comparison
    const [originalData, setOriginalData] = useState({ ...initialData });
    // Track if component is mounted
    const isMounted = useRef(false);

    // Set mounted flag on initial render
    useEffect(() => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
      };
    }, []);

    // Track editMode changes and sync with ref
    useEffect(() => {
      editModeRef.current = editMode;

      // Notify parent when edit mode changes
      if (onEditModeChange) {
        onEditModeChange(editMode);
      }
    }, [editMode, onEditModeChange]);

    // Sync with external editMode prop
    useEffect(() => {
      if (editMode !== initialEditMode) {
        setEditMode(initialEditMode);
      }
    }, [initialEditMode, editMode]);

    // Only run this effect on initial mount or when initialData changes significantly
    useEffect(() => {
      // Only reset form and edit mode on initial load or when initialData ID changes
      const dataId = initialData.organizationId || initialData.cmoFullName;
      const currentDataId =
        originalData.organizationId || originalData.cmoFullName;

      if (dataId !== currentDataId) {
        setForm({ ...initialData });
        setOriginalData({ ...initialData });
        // Don't reset editMode if it's already true
        if (!editModeRef.current) {
          setEditMode(false);
        }
        setErrors({});
        setIsModified(false);

        // Handle country and state conversion
        if (initialData.country) {
          const countries = Country.getAllCountries();
          const matchedCountry = countries.find(
            (c) =>
              c.name === initialData.country ||
              c.isoCode === initialData.country
          );

          if (matchedCountry) {
            setCountryCode(matchedCountry.isoCode);

            // Load states for this country
            const countryStates = State.getStatesOfCountry(
              matchedCountry.isoCode
            );
            setStates(countryStates);

            // Find matching state
            if (initialData.state) {
              const matchedState = countryStates.find(
                (s) =>
                  s.name === initialData.state ||
                  s.isoCode === initialData.state
              );

              if (matchedState) {
                setStateCode(matchedState.isoCode);

                // Load cities for this state
                const stateCities = City.getCitiesOfState(
                  matchedCountry.isoCode,
                  matchedState.isoCode
                );
                setCities(stateCities);
              }
            }
          }
        }
      }
    }, [initialData]);

    // Initialize country and state codes when component mounts
    useEffect(() => {
      // Only update if the codes are not already set
      if (!countryCode && initialData.country) {
        const countries = Country.getAllCountries();
        const matchedCountry = countries.find(
          (c) =>
            c.name === initialData.country || c.isoCode === initialData.country
        );

        if (matchedCountry) {
          setCountryCode(matchedCountry.isoCode);

          // Load states for this country
          const countryStates = State.getStatesOfCountry(
            matchedCountry.isoCode
          );
          setStates(countryStates);

          // Find matching state
          if (!stateCode && initialData.state) {
            const matchedState = countryStates.find(
              (s) =>
                s.name === initialData.state || s.isoCode === initialData.state
            );

            if (matchedState) {
              setStateCode(matchedState.isoCode);

              // Load cities for this state
              const stateCities = City.getCitiesOfState(
                matchedCountry.isoCode,
                matchedState.isoCode
              );
              setCities(stateCities);
            }
          }
        }
      }
    }, []);

    // Load states and cities when country/state changes
    useEffect(() => {
      if (countryCode) {
        const countryStates = State.getStatesOfCountry(countryCode);
        setStates(countryStates);

        // Get country name for the form
        const selectedCountry = Country.getCountryByCode(countryCode);
        if (selectedCountry) {
          setForm((prev) => ({ ...prev, country: selectedCountry.name }));
        }
      }
    }, [countryCode]);

    useEffect(() => {
      if (stateCode && countryCode) {
        const stateCities = City.getCitiesOfState(countryCode, stateCode);
        setCities(stateCities);

        // Get state name for the form
        const selectedState = State.getStateByCodeAndCountry(
          stateCode,
          countryCode
        );
        if (selectedState) {
          setForm((prev) => ({ ...prev, state: selectedState.name }));
        }
      }
    }, [stateCode, countryCode]);

    // Add effect to validate postal code when country or postal code changes
    useEffect(() => {
      if (form.country && form.postCode) {
        // Get country code from country name if needed
        let countryCode = form.country;
        if (!Country.getCountryByCode(form.country)) {
          const countryObj = Country.getAllCountries().find(
            (c) => c.name.toLowerCase() === String(form.country).toLowerCase()
          );
          countryCode = countryObj?.isoCode || form.country;
        }

        const result = postalCodes.validate(countryCode, form.postCode);
        setPostalCodeError(result === true ? "" : result);
      } else {
        setPostalCodeError("");
      }
    }, [form.country, form.postCode]);

    // Check if form has been modified
    useEffect(() => {
      const hasChanged = JSON.stringify(form) !== JSON.stringify(originalData);
      if (isModified !== hasChanged) {
        setIsModified(hasChanged);
        // Only notify parent of changes, don't pass the form data to avoid unnecessary re-renders
        if (onChange) onChange(hasChanged);
      }
    }, [form, originalData, onChange, isModified]);

    const handlePostalCodeChange = (e) => {
      // Apply consistent formatting to match the provided code
      // Only allow alphanumeric characters, spaces and hyphens
      // Replace multiple consecutive spaces or hyphens with a single one
      let value = e.target.value
        .replace(/[^a-zA-Z0-9\s-]/g, "")
        .replace(/(\s{2,}|-{2,})/g, (match) => match[0]);

      // Limit to 12 characters (standard for most postal codes worldwide)
      if (value.length > 12) {
        value = value.substring(0, 12);
      }

      // Update the form state
      setForm((prev) => ({ ...prev, postCode: value }));

      // Clear postCode error if it exists
      if (errors.postCode) {
        setErrors((prev) => ({ ...prev, postCode: "" }));
      }

      // Clear postalCodeError since we're changing the value
      setPostalCodeError("");

      // Validate against country-specific format if country is selected
      if (form.country && value) {
        // Get country code from country name if needed
        let countryCode = form.country;
        if (!Country.getCountryByCode(form.country)) {
          const countryObj = Country.getAllCountries().find(
            (c) => c.name.toLowerCase() === String(form.country).toLowerCase()
          );
          countryCode = countryObj?.isoCode || form.country;
        }

        const result = postalCodes.validate(countryCode, value);
        setPostalCodeError(result === true ? "" : result);
      }
    };

    const handleChange = (e) => {
      const { name, value } = e.target;

      if (name === "status") {
        if (form.status !== value) {
          setPendingStatus(value);
          setConfirmDialogOpen(true);
          return;
        }
      }

      // Create a new form object to avoid direct state mutation
      let updatedForm = { ...form };

      if (name === "country") {
        setCountryCode(value);
        setStateCode("");

        // We'll update the form country and state in the useEffect hooks
        // that respond to countryCode and stateCode changes

        // Reset city and post code when country changes
        updatedForm.city = "";
        updatedForm.postCode = ""; // Clear post code when country changes

        // Clear any postal code errors
        setPostalCodeError("");
      } else if (name === "state") {
        setStateCode(value);

        // We'll update the form state in the useEffect hook
        // that responds to stateCode changes

        // Reset city when state changes
        updatedForm.city = "";
      } else if (name === "city") {
        // For city dropdown, use the value directly
        updatedForm = { ...updatedForm, city: value };
      } else {
        updatedForm = { ...updatedForm, [name]: value };
      }

      // Update the form state
      setForm(updatedForm);

      // Real-time validation for individual fields
      let fieldErrors = { ...errors };

      switch (name) {
        case "cmoFullName":
          if (!value) {
            fieldErrors.cmoFullName = "CMO Full Name is required";
          } else if (!validateCmoFullName(value)) {
            fieldErrors.cmoFullName = "CMO Full Name can only contain letters, numbers, spaces, and special characters (&, -, ., ', /, (, ))";
          } else if (value.length > 255) {
            fieldErrors.cmoFullName = "CMO Full Name cannot exceed 255 characters";
          } else {
            fieldErrors.cmoFullName = "";
          }
          break;

        case "cmoShortName":
          if (!value) {
            fieldErrors.cmoShortName = "CMO Short Name is required";
          } else if (!validateCmoShortName(value)) {
            fieldErrors.cmoShortName = "CMO Short Name can only contain letters, numbers, hyphens (-), and underscores (_)";
          } else {
            fieldErrors.cmoShortName = "";
          }
          break;

        case "streetAddress1":
          if (!value) {
            fieldErrors.streetAddress1 = "Street Address 1 is required";
          } else if (!validateStreetAddress(value)) {
            fieldErrors.streetAddress1 = "Street Address 1 can only contain letters, numbers, spaces, and special characters (&, ., , -, /)";
          } else if (value.length > 255) {
            fieldErrors.streetAddress1 = "Street Address 1 cannot exceed 255 characters";
          } else {
            fieldErrors.streetAddress1 = "";
          }
          break;

        case "streetAddress2":
          if (value && !validateStreetAddress(value)) {
            fieldErrors.streetAddress2 = "Street Address 2 can only contain letters, numbers, spaces, and special characters (&, ., , -, /)";
          } else if (value && value.length > 255) {
            fieldErrors.streetAddress2 = "Street Address 2 cannot exceed 255 characters";
          } else {
            fieldErrors.streetAddress2 = "";
          }
          break;

        case "postCode":
          if (!value) {
            fieldErrors.postCode = "Post Code is required";
          } else if (updatedForm.country && postalCodeError) {
            fieldErrors.postCode = postalCodeError;
          } else if (!/^[a-zA-Z0-9\s-]{1,12}$/.test(value)) {
            fieldErrors.postCode = "Post Code can only contain letters, numbers, spaces, and hyphens (max 12 characters)";
          } else {
            fieldErrors.postCode = "";
          }
          break;

        case "city":
          fieldErrors.city = value ? "" : "City is required";
          break;

        case "state":
          fieldErrors.state = value ? "" : "State is required";
          break;

        case "country":
          fieldErrors.country = value ? "" : "Country is required";
          // Clear related field errors when parent field changes
          fieldErrors.state = "";
          fieldErrors.city = "";
          break;

        case "status":
          fieldErrors.status = value ? "" : "Status is required";
          break;

        default:
          break;
      }

      // Update errors state
      setErrors(fieldErrors);

      // Clear related field errors when parent field changes
      if (name === "country") {
        setErrors((prev) => ({ ...prev, state: "", city: "" }));
      } else if (name === "state") {
        setErrors((prev) => ({ ...prev, city: "" }));
      }
    };



    const createPayload = () => {
      // Check if only status has changed
      const statusChanged = form.status !== originalData.status;
      const otherFieldsChanged = Object.keys(form).some((key) => {
        // Skip status since we handle it separately
        if (key === "status") return false;
        // Check if any other field has changed
        return form[key] !== originalData[key];
      });

      // If only status has changed, create a simplified payload
      if (statusChanged && !otherFieldsChanged) {
        return {
          vendorMosymphonyID,
          brandOwnerMosymphonyID,

          updateStatusFlag: true,
          vendorPlantDetails: {
            status: form.status,
          },
        };
      }

      // Standard payload for other changes - this is the general case for address updates
      const payload = {
        vendorMosymphonyID,
        brandOwnerMosymphonyID,
        saasUserId: saasUserId,
        ecosystemID: Number(localStorage.getItem("ecosystemId")),
        cmoOrgDetails: {
          streetAddress1: form.streetAddress1,
          streetAddress2: form.streetAddress2,
          city: form.city,
          state: form.state,
          postCode: form.postCode,
          country: form.country,
          cmoFullName: form.cmoFullName,
          cmoShortName: form.cmoShortName,
        },
        updateCmoOrgDetailsFlag: true,
      };

      // Add status change flag if status changed, but we're also updating other fields
      if (statusChanged) {

        // Include status in vendorPlantDetails for combined updates
        payload.updateStatusFlag = true;
        if (!payload.vendorPlantDetails) {
          payload.vendorPlantDetails = {};
        }
        payload.vendorPlantDetails.status = form.status;
      }

      return payload;
    };

    const handleCancel = () => {
      // Create a fresh copy of initialData to avoid any reference issues
      const cleanInitialData = JSON.parse(JSON.stringify(initialData));
      setForm(cleanInitialData);
      setErrors({});
      setEditMode(false);

      // Reset country and state codes based on initial data
      if (initialData.country) {
        const countries = Country.getAllCountries();
        const matchedCountry = countries.find(
          (c) =>
            c.name === initialData.country || c.isoCode === initialData.country
        );

        if (matchedCountry) {
          setCountryCode(matchedCountry.isoCode);

          // Find matching state
          if (initialData.state) {
            const countryStates = State.getStatesOfCountry(
              matchedCountry.isoCode
            );
            const matchedState = countryStates.find(
              (s) =>
                s.name === initialData.state || s.isoCode === initialData.state
            );

            if (matchedState) {
              setStateCode(matchedState.isoCode);

              // Load cities for this state
              const stateCities = City.getCitiesOfState(
                matchedCountry.isoCode,
                matchedState.isoCode
              );
              setCities(stateCities);
            }
          }
        }
      }

      // Call parent's onDiscard if provided
      if (onDiscard) {
        onDiscard();
      }
    };

    // Confirm dialog handlers
    const handleConfirmStatusChange = () => {
      setForm((prev) => ({ ...prev, status: pendingStatus }));
      setConfirmDialogOpen(false);
      setPendingStatus(null);
    };

    const handleCancelStatusChange = () => {
      setConfirmDialogOpen(false);
      setPendingStatus(null);
    };

    const validate = () => {
      let valid = true;
      let errs = {};

      // CMO Full Name validation
      if (!form.cmoFullName) {
        errs.cmoFullName = "CMO Full Name is required";
        valid = false;
      } else if (!validateCmoFullName(form.cmoFullName)) {
        errs.cmoFullName = "CMO Full Name can only contain letters, numbers, spaces, and special characters (&, -, ., ', /, (, ))";
        valid = false;
      } else if (form.cmoFullName.length > 255) {
        errs.cmoFullName = "CMO Full Name cannot exceed 255 characters";
        valid = false;
      }

      // CMO Short Name validation  
      if (!form.cmoShortName) {
        errs.cmoShortName = "CMO Short Name is required";
        valid = false;
      } else if (!validateCmoShortName(form.cmoShortName)) {
        errs.cmoShortName = "CMO Short Name can only contain letters, numbers, hyphens (-), and underscores (_)";
        valid = false;
      }

      // Street Address 1 validation
      if (!form.streetAddress1) {
        errs.streetAddress1 = "Street Address 1 is required";
        valid = false;
      } else if (!validateStreetAddress(form.streetAddress1)) {
        errs.streetAddress1 = "Street Address 1 can only contain letters, numbers, spaces, and special characters (&, ., , -, /)";
        valid = false;
      } else if (form.streetAddress1.length > 255) {
        errs.streetAddress1 = "Street Address 1 cannot exceed 255 characters";
        valid = false;
      }

      // Street Address 2 validation (now required)
      if (!form.streetAddress2) {
        errs.streetAddress2 = "Street Address 2 is required";
        valid = false;
      } else if (!validateStreetAddress(form.streetAddress2)) {
        errs.streetAddress2 = "Street Address 2 can only contain letters, numbers, spaces, and special characters (&, ., , -, /)";
        valid = false;
      } else if (form.streetAddress2.length > 255) {
        errs.streetAddress2 = "Street Address 2 cannot exceed 255 characters";
        valid = false;
      }

      // City validation
      if (!form.city) {
        errs.city = "City is required";
        valid = false;
      }

      // State validation
      if (!form.state) {
        errs.state = "State is required";
        valid = false;
      }

      // Post Code validation
      if (!form.postCode) {
        errs.postCode = "Post Code is required";
        valid = false;
      } else if (form.country && postalCodeError) {
        // Add validation check for postalCodeError state (country-specific validation)
        errs.postCode = postalCodeError;
        valid = false;
      } else if (!/^[a-zA-Z0-9\s-]{1,12}$/.test(form.postCode)) {
        errs.postCode = "Post Code can only contain letters, numbers, spaces, and hyphens (max 12 characters)";
        valid = false;
      }

      // Country validation
      if (!form.country) {
        errs.country = "Country is required";
        valid = false;
      }

      // Status validation
      if (!form.status) {
        errs.status = "Status is required";
        valid = false;
      }

      setErrors(errs);
      return valid;
    };

    const handleSave = () => {
      if (validate()) {
        // Prepare the payload
        const payload = createPayload();

        // Call the parent's onSave with the form data and payload
        onSave(form, payload);

        // Update original data and reset states
        setOriginalData({ ...form });
        setIsModified(false);

        // Reset edit mode
        setEditMode(false);
        editModeRef.current = false;
      }
    };



    return (
      <div className="cmo-stepper-wrapper cmo-details-wrapper">
        <div className="stepper-container">
          <form>
            <div>
              <Grid className="two-col-forms-grid" container spacing={3}>
                {/* CMO Full Name */}
                <Grid item xs={12} sm={6}>
                  <Typography component="label" htmlFor="cmoFullName">
                    CMO Full Name 
                  </Typography>
                  <Typography>{form.cmoFullName || "-"}</Typography>
                </Grid>
                {/* CMO Short Name */}
                <Grid item xs={12} sm={6}>
                  <Typography component="label" htmlFor="cmoShortName">
                    CMO Short Name 
                  </Typography>
                  <Typography>{form.cmoShortName || "-"}</Typography>
                </Grid>
                {/* Street Address 1 */}
                <Grid item xs={12} sm={6} className="multiple-line-text">
                  <Typography component="label" htmlFor="streetAddress1">
                    Street Address 1 {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>{form.streetAddress1 || "-"}</Typography>
                  ) : (
                    <FormControl className={classes?.formField}>
                      <TextField
                        name="streetAddress1"
                        value={form.streetAddress1 || ""}
                        onChange={handleChange}
                        onBlur={(e) => {
                          const { name, value } = e.target;
                          // Trim leading and trailing spaces for string values
                          if (typeof value === "string" && value.trim() !== value) {
                            handleChange({ target: { name, value: value.trim() } });
                          }
                        }}
                        variant="outlined"
                        placeholder="Enter text here"
                        fullWidth
                        required
                        multiline
                        minRows={3}
                        maxRows={10}
                        error={!!errors.streetAddress1}
                        helperText={errors.streetAddress1}
                        InputProps={{ style: commonInlineStyles.input }}
                        sx={inputFieldStyles}
                        inputProps={{
                          ...commonInlineStyles.inputProps,
                          maxLength: 255,
                        }}
                        style={commonInlineStyles.input}
                        onInput={(e) => {
                          e.target.value = e.target.value
                            .replace(/[^a-zA-ZÀ-ÿ0-9\s&.,\-/]/g, "")
                            .replace(
                              /(\s{2,}|&{2,}|,-{2,}|,{2,}|\.{2,}|\/{2,}|'{2,}|\({2,}|\){2,})/g,
                              (match) => match[0]
                            );
                        }}
                      />
                    </FormControl>
                  )}
                </Grid>
                {/* Street Address 2 */}
                <Grid item xs={12} sm={6} className="multiple-line-text">
                  <Typography component="label">
                    Street Address 2 {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>{form.streetAddress2 || "-"}</Typography>
                  ) : (
                    <FormControl className={classes.formField}>
                      <TextField
                        name="streetAddress2"
                        value={form.streetAddress2 || ""}
                        onChange={handleChange}
                        onBlur={(e) => {
                          const { name, value } = e.target;
                          // Trim leading and trailing spaces for string values
                          if (typeof value === "string" && value.trim() !== value) {
                            handleChange({ target: { name, value: value.trim() } });
                          }
                        }}
                        variant="outlined"
                        placeholder="Enter text here"
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={10}
                        required
                        error={!!errors.streetAddress2}
                        helperText={errors.streetAddress2}
                        sx={inputFieldStyles}
                        inputProps={{
                          ...commonInlineStyles.inputProps,
                          maxLength: 255,
                        }}
                        style={commonInlineStyles.input}
                        onInput={(e) => {
                          e.target.value = e.target.value
                            .replace(/[^a-zA-ZÀ-ÿ0-9\s&.,\-/]/g, "")
                            .replace(
                              /(\s{2,}|&{2,}|,-{2,}|,{2,}|\.{2,}|\/{2,}|'{2,}|\({2,}|\){2,})/g,
                              (match) => match[0]
                            );
                        }}
                      />
                    </FormControl>
                  )}
                </Grid>
                {/* Country */}
                <Grid item xs={12} sm={6}>
                  <Typography component="label">
                    Country {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>{form.country || "-"}</Typography>
                  ) : (
                    <FormControl className={classes.formField}>
                      <Select
                        select
                        name="country"
                        value={countryCode || ""}
                        onChange={handleChange}
                        variant="outlined"
                        placeholder="Select country"
                        fullWidth
                        error={!!errors.country}
                        helperText={errors.country}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Select from the list
                        </MenuItem>
                        {Country.getAllCountries().map((country) => (
                          <MenuItem
                            key={country.isoCode}
                            value={country.isoCode}
                          >
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Grid>
                {/* State */}
                <Grid item xs={12} sm={6}>
                  <Typography component="label">
                    State {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>{form.state || "-"}</Typography>
                  ) : (
                    <FormControl className={classes.formField}>
                      <Select
                        select
                        name="state"
                        value={stateCode || ""}
                        onChange={handleChange}
                        variant="outlined"
                        placeholder="Select state"
                        fullWidth
                        disabled={!countryCode}
                        error={!!errors.state}
                        helperText={errors.state}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Select from the list
                        </MenuItem>
                        {states.map((state) => (
                          <MenuItem key={state.isoCode} value={state.isoCode}>
                            {state.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Grid>
                {/* City */}
                <Grid item xs={12} sm={6}>
                  <Typography component="label">
                    City {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>{form.city || "-"}</Typography>
                  ) : (
                    <FormControl className={classes.formField}>
                      <Select
                        select
                        name="city"
                        value={form.city || ""}
                        onChange={handleChange}
                        variant="outlined"
                        placeholder="Select city"
                        fullWidth
                        disabled={!stateCode || cities.length === 0}
                        error={!!errors.city}
                        helperText={errors.city}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Select from the list
                        </MenuItem>
                        {cities.map((city) => (
                          <MenuItem key={city.name} value={city.name}>
                            {city.name}
                          </MenuItem>
                        ))}
                        {/* If city isn't in the list but we have a value, add it as an option */}
                        {form.city &&
                          !cities.some((c) => c.name === form.city) && (
                            <MenuItem key={form.city} value={form.city}>
                              {form.city}
                            </MenuItem>
                          )}
                      </Select>
                    </FormControl>
                  )}
                </Grid>
                {/* Post Code */}
                <Grid item xs={12} sm={6}>
                  <Typography component="label">
                    Post Code {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>{form.postCode || "-"}</Typography>
                  ) : (
                    <FormControl className={classes.formField}>
                      <TextField
                        name="postCode"
                        value={form.postCode || ""}
                        onChange={handlePostalCodeChange}
                        onBlur={(e) => {
                          const { name, value } = e.target;
                          // Trim leading and trailing spaces for string values
                          if (typeof value === "string" && value.trim() !== value) {
                            handlePostalCodeChange({ target: { name, value: value.trim() } });
                          }
                        }}
                        variant="outlined"
                        placeholder="Enter text here"
                        fullWidth
                        required
                        error={Boolean(errors.postCode) || Boolean(postalCodeError)}
                        helperText={errors.postCode || postalCodeError || ""}
                        InputProps={{ style: commonInlineStyles.input }}
                        sx={inputFieldStyles}
                        inputProps={{
                          ...commonInlineStyles.inputProps,
                          maxLength: 12,
                        }}
                        style={commonInlineStyles.input}
                      />
                    </FormControl>
                  )}
                </Grid>
                {/* Status */}
                <Grid item xs={12} sm={6}>
                  <Typography component="label">
                    Status {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? (
                    <Typography>{form.status || "-"}</Typography>
                  ) : (
                    <FormControl className={classes.formField}>
                      <TextField
                        select
                        name="status"
                        value={form.status || ""}
                        onChange={handleChange}
                        variant="outlined"
                        placeholder="Select status"
                        fullWidth
                        error={!!errors.status}
                        helperText={errors.status}
                      >
                        {statusOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </TextField>
                    </FormControl>
                  )}
                </Grid>
                {/* Organization Code */}
                <Grid item xs={12} sm={6}>
                  <Typography component="label">Organization Code</Typography>
                  {/* {!editMode ?  */}
                  <Typography>{form.organizationCode || "-"}</Typography>
                  {/* : <FormControl className={classes.formField}>
              <TextField
                name="organizationCode"
                value={form.organizationCode || ""}
                onChange={handleChange}
                variant="outlined"
                placeholder="Enter organization code"
                fullWidth
                error={!!errors.organizationCode}
                helperText={errors.organizationCode}
              />
            </FormControl>} */}
                </Grid>
                {/* Organization ID */}
                {/* <Grid item xs={12} sm={6}>
              <Typography className={classes.label}>Organization ID</Typography>
            {!editMode ? <div className={classes.readOnlyValue}>{form.organizationId || "-"}</div> : <FormControl className={classes.formField}>
              <TextField
                name="organizationId"
                value={form.organizationId || ""}
                onChange={handleChange}
                variant="outlined"
                placeholder="Enter organization ID"
                fullWidth
                error={!!errors.organizationId}
                helperText={errors.organizationId}
              />
            </FormControl>}
          </Grid> */}
              </Grid>

              {/* Status change confirmation dialog */}
              <Dialog
                open={confirmDialogOpen}
                onClose={handleCancelStatusChange}
                maxWidth="sm"
                fullWidth
                className="status-change-dialog"
              >
                
                <DialogContent>
                  <Typography variant="h5" className={classes.title}>
                  Are you sure you want to <span>{pendingStatus === "Inactive" ? "Deactivate" : "Reactivate"}</span> the Record?
                  </Typography>

                  <Typography className={classes.titleText} style={{textAlign: "center"}}>This action will change the status to {`${pendingStatus === "Inactive" ? "inactive and will not allow" : "active and will allow"}  user to login`}</Typography>
                 
                </DialogContent>
                <DialogActions style={{ justifyContent: 'center', padding: '20px 24px', marginBottom: 20, marginTop: 20, gap: '20px' }}>
                <Button 
                    onClick={handleCancelStatusChange}
                    className={classes.noButton}
                  >
                    Cancel <ArrowForwardIosIcon style={{fontSize: 15, marginLeft: 5}} />
                  </Button>
                  <Button 
                    onClick={handleConfirmStatusChange}
                    className={classes.yesButton}
                    autoFocus
                  >
                     Confirm {pendingStatus === "Inactive" ? "Deactivation" : "Reactivation"} <ArrowForwardIosIcon style={{fontSize: 15, marginLeft: 5}} />
                  </Button>
                  
                </DialogActions>
              </Dialog>
            </div>
          </form>
          <Divider style={{ margin: "80px 00px 0px 00px", backgroundColor: '#707070', height: 1, }} />

        </div>
      </div>
    );
  }
);

export default CMODetailsSection;
